/* eslint-env browser */
// Users › Drivers, as the approved W2_Portal_Drivers board draws it: every
// filter is a chip with its count, active filters show as removable chips
// above the table next to an always-visible count, and each row shows what
// agents check (joined, car or placeholder, approval, online, app version,
// missing documents or permissions) in words, not colour alone. Everything
// runs in memory on the drivers already loaded (staticusers): no new reads.
// Call-centre feedback (29 Oct): the search box clears with an X, a shown
// phone number has a copy button (copied without spaces, for MicroSIP), the
// filter chips hide and show (kept per person; the active filters stay as
// chips above the table), and every row has "Add note", which opens the
// driver's notes with the box ready to type (components/QuickDriverNote.js;
// the notes are read only while it is open).
import React, { useState, useEffect, useMemo, useRef, useDeferredValue } from "react";
import { pressPoint, selectingTextIn } from "../common/textSelection";
import { useSelector, useDispatch } from "react-redux";
import { api } from "common";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation, Link as RouterLink } from "react-router-dom";
import { usePageState, onPageStateReset } from "../components/modern/pageState";
import SearchField from "../components/modern/SearchField";
import CopyButton from "../components/modern/CopyButton";
import IconCircle from "../components/modern/IconCircle";
import { useFiltersOpen, FilterToggleButton } from "../components/modern/FilterToggle";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import Switch from "@mui/material/Switch";
import Avatar from "@mui/material/Avatar";
import Link from "@mui/material/Link";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import TablePagination from "@mui/material/TablePagination";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import DirectionsCarRoundedIcon from "@mui/icons-material/DirectionsCarRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import GppMaybeOutlinedIcon from "@mui/icons-material/GppMaybeOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import { tokens as c, toneChipSx } from "../theme";
import { showToast, confirmDialog, InlineMessage, EmptyState, TableSkeletonRows } from "../components/feedback";
import { exportCsv, printTable, datedFileName, formatAddisDate, formatAddisTime, formatAddisDateTime } from "../common/exportUtils";
import QuickDriverNote from "../components/QuickDriverNote";
import { permissionStatus, permissionMissingCount, permissionSummary, PERM_STATES } from "../components/driverPermissions";
import { markCarsStale } from "../utils/carsSession";

const ALL = "all";
const NO_CAR_TYPE = "__none";
const EMPTY_FILTERS = {
  carType: ALL,
  approval: ALL,
  online: ALL,
  carInfo: ALL,
  licence: ALL,
  tin: ALL,
  permissions: ALL,
  notes: ALL,
  agent: ALL,
  contacts: ALL,
};
const FILTER_KEYS = Object.keys(EMPTY_FILTERS);
const MORE_FILTER_KEYS = ["licence", "tin", "permissions", "notes", "agent", "contacts"];
const ROWS_OPTIONS = [25, 50, 100];
const SORT_KEYS = ["driver", "email", "joined", "car", "approved", "online", "app", "compliance"];
const OPTIONAL_COLUMNS = ["email", "joined", "car", "approved", "online", "app", "compliance"];
// Per-viewer conveniences only (browser storage, never shared): rows per page
// and hidden columns stay for the next visit; filters, search and sort last
// for the tab, so "Back" from a driver returns to the same call list and page.
const VIEW_PREFS_KEY = "dd.drivers.view.v1";
const FILTERS_KEY = "dd.drivers.filters.v1";
// The filters kept for this tab go with the other lists' at sign-out.
onPageStateReset(() => {
  try { window.sessionStorage.removeItem(FILTERS_KEY); } catch (e) { /* storage blocked */ }
});

const EDIT_ROLES = ["admin", "callcenteragent", "callcentersupervisor"];
const ONBOARD_ROLES = ["admin", "callcenteragent", "callcentersupervisor"];
const DOCUMENT_ROLES = ["callcenteragent", "callcentersupervisor", "admin", "finance"];
const NOTES_ROLES = ["admin", "callcenteragent", "callcentersupervisor", "finance", "marketing"];

const NUMBER_FORMAT = new Intl.NumberFormat("en-US");
const fmt = (n) => NUMBER_FORMAT.format(n || 0);
// One collator for the text sorts: 12,000 rows sort without building a new
// one per comparison.
const COLLATOR = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });
const byText = (a, b) => COLLATOR.compare(a, b);

const srOnly = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

const storageOf = (kind) => {
  try {
    return kind === "local" ? window.localStorage : window.sessionStorage;
  } catch (e) {
    return null;
  }
};
const readJson = (kind, key) => {
  try {
    const store = storageOf(kind);
    const raw = store ? store.getItem(key) : null;
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};
const writeJson = (kind, key, value) => {
  try {
    const store = storageOf(kind);
    if (store) store.setItem(key, JSON.stringify(value));
  } catch (e) {
    // private window or blocked storage: the view still works, it just forgets
  }
};

const toMs = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return value;
  if (/^\d{9,}$/.test(String(value).trim())) return Number(value);
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// "+251912345678" / "0912345678" / "912345678" -> "912345678"
const onlyDigits = (value) => String(value || "").replace(/\D/g, "");
const localDigits = (digits) => {
  let d = digits;
  if (d.indexOf("251") === 0) d = d.slice(3);
  if (d.indexOf("0") === 0) d = d.slice(1);
  return d;
};
// No spaces (owner, 29 Oct 2026): "+251920439275", never the US-style "+251 92 043 9275" - in the portal every number reads as stored and pastes straight into MicroSIP (see components/modern/tableUtils.formatPhoneDisplay).
const formatPhone = (mobile) => {
  const m = /^\+251(\d{9})$/.exec(String(mobile || "").replace(/[\s-]/g, "").trim());
  return m ? `+251${m[1]}` : mobile || "";
};

const nameOf = (d) => `${d.firstName || ""} ${d.lastName || ""}`.trim();
const initialsOf = (d) =>
  [d.firstName, d.lastName]
    .map((n) => String(n || "").trim().charAt(0))
    .join("")
    .toUpperCase();

const compareVersions = (a, b) => {
  const parse = (v) => String(v || "0").split(".").map((n) => parseInt(n, 10) || 0);
  const pa = parse(a);
  const pb = parse(b);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const diff = (pa[i] || 0) - (pb[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
};

// permissionStatus() only needs labels for display; counting passes the
// English defaults straight through so 12,000 rows cost no t() calls.
const NO_LABELS = (key, dflt) => dflt;

// Everything the filters, counts, sorting and cells need, worked out once per
// driver when the list arrives.
const driverFacts = (d) => {
  const hasCar = !!(d.vehicleNumber && d.carType && d.vehicleMake && d.vehicleModel);
  // Placeholder car info (Security → "Fill Vehicle Placeholders"): a car type
  // so the driver can work, but the real plate is still missing.
  const placeholder = !hasCar && api.vehiclePlaceholderPending(d);
  const hasLicence = !!(d.licenseImage && d.licenseImageBack);
  const hasTin = !!d.tin;
  // Reported drivers with at least one missing / partial grant count as
  // "Missing"; unreported drivers are left out (nothing to call them about).
  const permReported = !!d.permCheckedAt;
  const permOff = permReported ? permissionMissingCount(d, NO_LABELS) : 0;
  return {
    carType: d.carType ? String(d.carType) : NO_CAR_TYPE,
    carInfo: hasCar ? "has" : placeholder ? "placeholder" : "missing",
    approval: d.approved ? "approved" : "waiting",
    online: d.driverActiveStatus ? "online" : "offline",
    licence: hasLicence ? "has" : "missing",
    tin: hasTin ? "has" : "missing",
    permissions: permReported && permOff > 0 ? "missing" : "ok",
    notes: d.lastContactDate || d.lastContactType || d.lastContactAgent ? "with" : "without",
    agent: d.fleetadmin ? "agent" : "self",
    contacts: d.hasInvitedContacts === true ? "invited" : "not_invited",
    hasLicence,
    hasTin,
    permReported,
    permOff,
    issues: (hasLicence ? 0 : 1) + (hasTin ? 0 : 1) + permOff,
  };
};

const SORTERS = {
  driver: (a, b) => byText(nameOf(a.d), nameOf(b.d)),
  email: (a, b) => byText(String(a.d.email || ""), String(b.d.email || "")),
  joined: (a, b) => toMs(a.d.createdAt) - toMs(b.d.createdAt),
  car: (a, b) => byText(`${a.d.carType || "~"} ${a.d.vehicleNumber || ""}`, `${b.d.carType || "~"} ${b.d.vehicleNumber || ""}`),
  approved: (a, b) => (a.d.approved ? 1 : 0) - (b.d.approved ? 1 : 0),
  online: (a, b) =>
    (a.d.driverActiveStatus ? 2 : 0) + (a.d.queue ? 1 : 0) - ((b.d.driverActiveStatus ? 2 : 0) + (b.d.queue ? 1 : 0)),
  app: (a, b) => compareVersions(a.d.appVersion, b.d.appVersion),
  compliance: (a, b) => a.f.issues - b.f.issues,
};

const buildQuery = (raw) => {
  const text = String(raw || "").trim().toLowerCase();
  if (!text) return null;
  const digits = onlyDigits(text);
  const phoneLike = /^[+\d\s().-]+$/.test(text) && digits.length >= 3;
  return {
    text,
    compact: text.replace(/[\s-]+/g, ""),
    phone: phoneLike ? localDigits(digits) || digits : "",
  };
};

const matchesQuery = (d, q, allowPrivate) => {
  if (!q) return true;
  if (q.phone && allowPrivate && localDigits(onlyDigits(d.mobile)).includes(q.phone)) return true;
  if (nameOf(d).toLowerCase().includes(q.text)) return true;
  const plate = String(d.vehicleNumber || "").toLowerCase().replace(/[\s-]+/g, "");
  if (plate && q.compact && plate.includes(q.compact)) return true;
  if (allowPrivate && String(d.email || "").toLowerCase().includes(q.text)) return true;
  return false;
};

// The values each filter can take (car type is free text from the car types
// list), so a stale saved filter can never hide every row without a chip.
const FILTER_VALUES = {
  approval: ["approved", "waiting"],
  online: ["online", "offline"],
  carInfo: ["has", "missing", "placeholder"],
  licence: ["has", "missing"],
  tin: ["has", "missing"],
  permissions: ["missing"],
  notes: ["with", "without"],
  agent: ["agent", "self"],
  contacts: ["invited", "not_invited"],
};

const sanitizeFilters = (saved) => {
  const next = { ...EMPTY_FILTERS };
  if (saved && typeof saved === "object") {
    FILTER_KEYS.forEach((k) => {
      const v = saved[k];
      if (typeof v !== "string" || !v) return;
      if (k === "carType" || FILTER_VALUES[k].includes(v)) next[k] = v;
    });
  }
  return next;
};

const stop = (handler) => (event) => {
  if (event && event.stopPropagation) event.stopPropagation();
  if (handler) handler(event);
};

// One chip of a filter group: 44 px, its count beside the words, a check
// when selected (never colour alone).
function FilterChip({ selected, label, count, dot, onClick }) {
  return (
    <Chip
      clickable
      onClick={onClick}
      aria-pressed={selected ? "true" : "false"}
      icon={
        selected ? (
          <CheckRoundedIcon />
        ) : dot ? (
          <Box component="span" aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: dot, flex: "none" }} />
        ) : undefined
      }
      label={
        <Box component="span" sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.75 }}>
          <span>{label}</span>
          {count !== undefined && count !== null ? (
            <Box component="span" sx={{ fontWeight: 400, color: selected ? c.ACCENT_TEXT : c.INK_2, fontVariantNumeric: "tabular-nums" }}>
              {fmt(count)}
            </Box>
          ) : null}
        </Box>
      }
      sx={{
        height: 44,
        borderRadius: "22px",
        px: 0.75,
        fontSize: 15,
        fontWeight: 600,
        bgcolor: selected ? c.ACCENT_TINT : c.SURFACE,
        color: selected ? c.ACCENT_TEXT : c.INK,
        border: `${selected ? 1.5 : 1}px solid ${selected ? c.ACCENT : c.OUTLINE_STRONG}`,
        "& .MuiChip-icon": { color: c.ACCENT_TEXT, fontSize: 18, ml: 0.75, mr: -0.25 },
        "& .MuiChip-label": { px: 1 },
        "&.MuiChip-clickable:hover": { bgcolor: selected ? c.ACCENT_TINT : c.SURFACE_ALT },
        "&.Mui-focusVisible": { outline: `2px solid ${c.ACCENT}`, outlineOffset: 2 },
      }}
    />
  );
}

function ChipGroup({ label, value, options, onChange }) {
  return (
    <Box role="group" aria-label={label} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
      <Typography component="span" sx={{ fontSize: 15, fontWeight: 600, color: c.INK_2, lineHeight: 1.4, mr: 0.5 }}>
        {label}
      </Typography>
      {options.map((o) => (
        <FilterChip
          key={o.value}
          selected={value === o.value}
          label={o.label}
          count={o.count}
          dot={o.dot}
          onClick={() => onChange(o.value)}
        />
      ))}
    </Box>
  );
}

// "Licence: All ▾": the less-used filters as one chip each with a menu.
function DropChip({ label, value, options, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const current = options.find((o) => o.value === value) || options[0];
  const active = value !== ALL;
  return (
    <>
      <Chip
        clickable
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={anchor ? "true" : "false"}
        icon={active ? <CheckRoundedIcon /> : undefined}
        label={
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
            <span>{label}:</span>
            <Box component="span" sx={{ fontWeight: active ? 700 : 400 }}>
              {current.label}
            </Box>
            <ExpandMoreRoundedIcon aria-hidden="true" sx={{ fontSize: 18, ml: 0.25 }} />
          </Box>
        }
        sx={{
          height: 44,
          borderRadius: "22px",
          px: 0.5,
          fontSize: 15,
          fontWeight: 600,
          bgcolor: active ? c.ACCENT_TINT : c.SURFACE,
          color: active ? c.ACCENT_TEXT : c.INK,
          border: `${active ? 1.5 : 1}px solid ${active ? c.ACCENT : c.OUTLINE_STRONG}`,
          "& .MuiChip-icon": { color: c.ACCENT_TEXT, fontSize: 18, ml: 0.75, mr: -0.25 },
          "& .MuiChip-label": { px: 1 },
          "&.MuiChip-clickable:hover": { bgcolor: active ? c.ACCENT_TINT : c.SURFACE_ALT },
          "&.Mui-focusVisible": { outline: `2px solid ${c.ACCENT}`, outlineOffset: 2 },
        }}
      />
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {options.map((o) => (
          <MenuItem
            key={o.value}
            selected={o.value === value}
            onClick={() => {
              setAnchor(null);
              onChange(o.value);
            }}
            sx={{ minWidth: 220, gap: 1 }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {o.value === value ? <CheckRoundedIcon fontSize="small" sx={{ color: c.ACCENT_TEXT }} /> : null}
            </ListItemIcon>
            <ListItemText primary={o.label} />
            {o.count !== undefined && o.count !== null ? (
              <Typography variant="body2" sx={{ color: c.INK_2, ml: 2, fontVariantNumeric: "tabular-nums" }}>
                {fmt(o.count)}
              </Typography>
            ) : null}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

// ✓ Licence / ✗ TIN: the icon plus the word, and the state in words for
// screen readers.
function DocWord({ ok, label, okText, missingText }) {
  const Icon = ok ? CheckRoundedIcon : CloseRoundedIcon;
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: c.INK, fontSize: 14, lineHeight: 1.45 }}>
      <Icon aria-hidden="true" sx={{ fontSize: 18, color: ok ? c.SUCCESS_TEXT : c.DANGER_TEXT }} />
      <span>{label}</span>
      <Box component="span" sx={srOnly}>
        {ok ? okText : missingText}
      </Box>
    </Box>
  );
}

export default function Users() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { deleteUser, fetchUsersOnce, updateUserFields } = api;
  const [data, setData] = useState([]);
  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const usersError = useSelector((state) => state.usersdata.error);
  const usersLoading = useSelector((state) => state.usersdata.loading);
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);
  const dispatch = useDispatch();
  const { state } = useLocation();
  const role = auth.profile ? auth.profile.usertype : null;
  const allowPrivate = !!settings.AllowCriticalEditsAdmin;
  // The approve / online / busy switches were enabled only for an admin with
  // critical edits on; everyone else sees the state as words.
  const canSwitch = allowPrivate && role === "admin";

  const savedView = useMemo(() => readJson("local", VIEW_PREFS_KEY) || {}, []);
  const savedFilters = useMemo(() => readJson("session", FILTERS_KEY) || {}, []);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_OPTIONS.includes(savedView.rowsPerPage) ? savedView.rowsPerPage : 25);
  const [hiddenCols, setHiddenCols] = useState(
    Array.isArray(savedView.hidden) ? savedView.hidden.filter((k) => OPTIONAL_COLUMNS.includes(k)) : ["email"]
  );
  const [filters, setFilters] = useState(() => sanitizeFilters(savedFilters.filters));
  const [search, setSearch] = useState(typeof savedFilters.search === "string" ? savedFilters.search : "");
  const [sort, setSort] = useState(
    savedFilters.sort && SORT_KEYS.includes(savedFilters.sort.by)
      ? { by: savedFilters.sort.by, dir: savedFilters.sort.dir === "asc" ? "asc" : "desc" }
      : { by: "joined", dir: "desc" }
  );
  // The page is kept too (the filters, search and sort already are, above),
  // also when the browser's own Back brings staff here without a pageNo.
  const [page, setPage] = usePageState("drivers", "page", Number.isInteger(state?.pageNo) && state.pageNo >= 0 ? state.pageNo : 0);
  const [columnsAnchor, setColumnsAnchor] = useState(null);
  const [rowMenu, setRowMenu] = useState(null); // { anchor, row }
  // The driver whose notes are open. Looked up in the loaded list, so a note
  // saved there (lastContact* mirrored into the users) shows in the header.
  const [notesId, setNotesId] = useState(null);
  // Filter chips shown or hidden, kept per person in this browser. Hidden
  // filters stay applied: the chips above the table still list them.
  const [filtersOpen, toggleFilters] = useFiltersOpen("drivers");

  useEffect(() => {
    if (Number.isInteger(state?.pageNo) && state.pageNo >= 0) setPage(state.pageNo);
  }, [state]);

  useEffect(() => {
    dispatch(fetchUsersOnce());
  }, [dispatch, fetchUsersOnce]);

  useEffect(() => {
    if (staticusers) {
      setData(
        staticusers.filter(
          (user) =>
            user.usertype === "driver" &&
            ((user.fleetadmin === auth.profile.uid && auth.profile.usertype === "fleetadmin") ||
              auth.profile.usertype === "admin" ||
              auth.profile.usertype === "finance" ||
              auth.profile.usertype === "callcenteragent" ||
              auth.profile.usertype === "callcentersupervisor")
        )
      );
    } else {
      setData([]);
    }
  }, [staticusers, auth.profile.usertype, auth.profile.uid]);

  // A new filter, search or sort starts at the first page (but a page number
  // handed back by "Back" from a driver is kept: only a real change resets).
  const listSignature = JSON.stringify([filters, search.trim(), sort]);
  const lastSignature = useRef(listSignature);
  const rowPressRef = useRef(null); // where the last row press began (a drag selects text)
  useEffect(() => {
    if (lastSignature.current === listSignature) return;
    lastSignature.current = listSignature;
    setPage(0);
  }, [listSignature]);

  useEffect(() => {
    writeJson("session", FILTERS_KEY, { filters, search, sort });
  }, [filters, search, sort]);

  useEffect(() => {
    writeJson("local", VIEW_PREFS_KEY, { rowsPerPage, hidden: hiddenCols });
  }, [rowsPerPage, hiddenCols]);

  // The error flag in the users slice is never cleared by a new fetch, so a
  // fetch in flight counts as loading, not failed.
  const listFailed = !staticusers && !usersLoading && !!(usersError && usersError.flag);
  const listLoading = !staticusers && !listFailed;

  const enriched = useMemo(() => data.map((d) => ({ d, f: driverFacts(d) })), [data]);
  const notesRow = useMemo(() => (notesId ? data.find((d) => d.id === notesId) || null : null), [notesId, data]);
  // Typing in the search box stays quick on 12,000 drivers: the list follows
  // the text a moment later instead of on every key.
  const deferredSearch = useDeferredValue(search);

  // Counts are totals over all loaded drivers (not narrowed by the other
  // filters), so every group adds up to the same total.
  const counts = useMemo(() => {
    const out = {};
    FILTER_KEYS.forEach((k) => {
      out[k] = {};
    });
    enriched.forEach(({ f }) => {
      FILTER_KEYS.forEach((k) => {
        out[k][f[k]] = (out[k][f[k]] || 0) + 1;
      });
    });
    return out;
  }, [enriched]);

  const visible = useMemo(() => {
    const q = buildQuery(deferredSearch);
    const list = enriched.filter(
      (w) => FILTER_KEYS.every((k) => filters[k] === ALL || w.f[k] === filters[k]) && matchesQuery(w.d, q, allowPrivate)
    );
    const cmp = SORTERS[sort.by] || SORTERS.joined;
    const dir = sort.dir === "asc" ? 1 : -1;
    list.sort((a, b) => cmp(a, b) * dir || toMs(b.d.createdAt) - toMs(a.d.createdAt));
    return list;
  }, [enriched, filters, deferredSearch, sort, allowPrivate]);

  const L = (key, dflt) => t(key, { defaultValue: dflt });
  const tx = {
    all: L("all", "All"),
    carType: L("drivers_filter_car_type", "Car type"),
    none: L("drivers_none", "None"),
    approval: L("drivers_filter_approval", "Approval"),
    approved: L("drivers_approved", "Approved"),
    waiting: L("drivers_waiting", "Waiting"),
    online: L("drivers_filter_online", "Online"),
    onlineNow: L("drivers_online_now", "Online now"),
    offline: L("drivers_offline", "Offline"),
    carInfo: L("drivers_filter_car_info", "Car info"),
    hasCar: L("drivers_has_car", "Has car"),
    missing: L("drivers_missing", "Missing"),
    placeholder: L("drivers_placeholder", "Placeholder"),
    licence: L("drivers_licence", "Licence"),
    hasLicence: L("drivers_has_licence", "Has licence"),
    tin: L("drivers_tin", "TIN"),
    hasTin: L("drivers_has_tin", "Has TIN"),
    permissions: L("drivers_permissions", "Permissions"),
    notes: L("notes", "Notes"),
    withNotes: L("drivers_with_notes", "With notes"),
    withoutNotes: L("drivers_without_notes", "Without notes"),
    agent: L("agent", "Agent"),
    withAgent: L("drivers_with_agent", "With agent"),
    selfOnboarded: L("drivers_self_onboarded", "Self-onboarded"),
    contacts: L("drivers_contacts", "Contacts"),
    invited: L("drivers_invited", "Invited contacts"),
    notInvited: L("drivers_not_invited", "Not invited"),
    more: L("more", "More"),
    search: L("search", "Search"),
    ok: L("drivers_ok", "OK"),
    // "drivers_on_trip" is taken by the dashboard ("Drivers On Trip").
    onTrip: L("drivers_state_on_trip", "On a trip"),
    free: L("drivers_state_free", "Free"),
  };

  const option = (group, value, label, extra) => ({
    value,
    label,
    count: value === ALL || listLoading ? undefined : (counts[group] && counts[group][value]) || 0,
    ...(extra || {}),
  });

  const carTypeValues = Object.keys(counts.carType || {})
    .filter((v) => v !== NO_CAR_TYPE)
    .sort((a, b) => (counts.carType[b] || 0) - (counts.carType[a] || 0) || a.localeCompare(b));
  if (filters.carType !== ALL && filters.carType !== NO_CAR_TYPE && !carTypeValues.includes(filters.carType)) {
    carTypeValues.push(filters.carType);
  }
  const groups = {
    carType: {
      label: tx.carType,
      options: [
        option("carType", ALL, tx.all),
        ...carTypeValues.map((v) => option("carType", v, v)),
        ...((counts.carType && counts.carType[NO_CAR_TYPE]) || filters.carType === NO_CAR_TYPE
          ? [option("carType", NO_CAR_TYPE, tx.none)]
          : []),
      ],
    },
    approval: {
      label: tx.approval,
      options: [option("approval", ALL, tx.all), option("approval", "approved", tx.approved), option("approval", "waiting", tx.waiting)],
    },
    online: {
      label: tx.online,
      options: [option("online", ALL, tx.all), option("online", "online", tx.onlineNow), option("online", "offline", tx.offline)],
    },
    carInfo: {
      label: tx.carInfo,
      options: [
        option("carInfo", ALL, tx.all),
        option("carInfo", "has", tx.hasCar),
        option("carInfo", "missing", tx.missing),
        option("carInfo", "placeholder", tx.placeholder, { dot: c.WARNING_TEXT }),
      ],
    },
    licence: {
      label: tx.licence,
      options: [option("licence", ALL, tx.all), option("licence", "has", tx.hasLicence), option("licence", "missing", tx.missing)],
    },
    tin: {
      label: tx.tin,
      options: [option("tin", ALL, tx.all), option("tin", "has", tx.hasTin), option("tin", "missing", tx.missing)],
    },
    permissions: {
      label: tx.permissions,
      options: [option("permissions", ALL, tx.all), option("permissions", "missing", tx.missing)],
    },
    notes: {
      label: tx.notes,
      options: [option("notes", ALL, tx.all), option("notes", "with", tx.withNotes), option("notes", "without", tx.withoutNotes)],
    },
    agent: {
      label: tx.agent,
      options: [option("agent", ALL, tx.all), option("agent", "agent", tx.withAgent), option("agent", "self", tx.selfOnboarded)],
    },
    contacts: {
      label: tx.contacts,
      options: [option("contacts", ALL, tx.all), option("contacts", "invited", tx.invited), option("contacts", "not_invited", tx.notInvited)],
    },
  };

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const clearAll = () => {
    setFilters({ ...EMPTY_FILTERS });
    setSearch("");
  };

  const searchText = search.trim();
  const activeTokens = FILTER_KEYS.filter((k) => filters[k] !== ALL).map((k) => {
    const opt = groups[k].options.find((o) => o.value === filters[k]);
    return { key: k, group: groups[k].label, value: opt ? opt.label : filters[k] };
  });
  const tokens = searchText ? [...activeTokens, { key: "search", group: tx.search, value: `“${searchText}”` }] : activeTokens;
  const removeToken = (key) => (key === "search" ? setSearch("") : setFilter(key, ALL));
  const tokenText = tokens.map((tk) => `${tk.group}: ${tk.value}`).join(" · ");

  const total = data.length;
  const countText = tokens.length
    ? t("drivers_count_shown", {
        count: total,
        shown: fmt(visible.length),
        total: fmt(total),
        defaultValue_one: "{{shown}} of {{total}} driver",
        defaultValue_other: "{{shown}} of {{total}} drivers",
        defaultValue: "{{shown}} of {{total}} drivers",
      })
    : t("drivers_count_total", {
        count: total,
        total: fmt(total),
        defaultValue_one: "{{total}} driver",
        defaultValue_other: "{{total}} drivers",
        defaultValue: "{{total}} drivers",
      });

  // The three switches write ONLY their own fields (RTDB update). They used
  // to spread the cached row into editUser(), which replaces the whole
  // profile: every field written server-side since the cache was filled
  // (carApproved, vehiclePlaceholder, term, commissionMode, credit and SMS
  // stamps…) was wiped, and the computed permSummary column was written in.
  const applyPatch = (rowData, patch) => {
    dispatch(updateUserFields(rowData.id, patch));
    setData((prev) => prev.map((d) => (d.id === rowData.id ? { ...d, ...patch } : d)));
  };
  // adminApprovedTrue = "an admin approved this driver", so it follows the
  // switch. It used to be written true on UN-approve too, and the role switch
  // trusts it: an un-approved driver could switch to customer and back and
  // come back approved.
  const handelApproved = (rowData) => {
    applyPatch(rowData, { approved: !rowData.approved, adminApprovedTrue: !rowData.approved });
  };
  const handelDriverActiveStatus = (rowData) => {
    applyPatch(rowData, {
      driverActiveStatus: !rowData.driverActiveStatus,
      // Admin switching a driver OFF is a deliberate offline: the server's
      // auto-online pass respects driverOfflineAt for a few hours.
      ...(rowData.driverActiveStatus ? { driverOfflineAt: Date.now() } : {}),
    });
  };

  const handleQueueStatus = (rowData) => {
    applyPatch(rowData, { queue: !rowData.queue });
  };

  // The page shown (a remembered page past the end of a shorter list falls
  // back to the last page); "Back" from a driver returns to it.
  const maxPage = Math.max(0, Math.ceil(visible.length / rowsPerPage) - 1);
  const safePage = Math.min(page, maxPage);
  const navState = { state: { pageNo: safePage } };
  const goDetails = (row) => navigate(`/users/driverdetails/${row.id}`, navState);
  const goEdit = (row) => navigate(`/users/edituser/driver/${row.id}`, navState);
  const goDocuments = (row) => navigate(`/users/userdocuments/${id}/${row.id}`, navState);

  const closeRowMenu = () => setRowMenu(null);

  const handleDelete = async (row) => {
    closeRowMenu();
    if (!allowPrivate) {
      showToast({ message: t("demo_mode"), kind: "info" });
      return;
    }
    const name = nameOf(row) || formatPhone(row.mobile);
    const ok = await confirmDialog({
      title: t("drivers_delete_title", { name, defaultValue: "Delete driver {{name}}?" }),
      body: t("drivers_delete_body", {
        defaultValue: "This removes the driver's account and their cars. It cannot be undone.",
      }),
      confirmLabel: t("delete", { defaultValue: "Delete" }),
      cancelLabel: t("confirm_cancel", { defaultValue: "Cancel" }),
      destructive: true,
    });
    if (!ok) return;
    // deleteUser also removes the driver's /cars rows (after two reads): make
    // the cars list refetch once — marked now and again when the removes are
    // issued.
    markCarsStale();
    setData((prev) => prev.filter((d) => d.id !== row.id));
    Promise.resolve(dispatch(deleteUser(row.id))).then(
      () => {
        markCarsStale();
        showToast({ message: t("drivers_deleted", { defaultValue: "Driver deleted" }), kind: "success" });
      },
      () => {
        markCarsStale();
        setData((prev) => (prev.some((d) => d.id === row.id) ? prev : [...prev, row]));
        showToast({ message: t("drivers_delete_failed", { defaultValue: "Could not delete the driver. Try again." }), kind: "error" });
      }
    );
  };

  // Exports (admin only, as before: CSV with critical edits on, print/PDF
  // always) go through the Ethiopic-safe helper: UTF-8 CSV with a BOM,
  // Addis Ababa 12-hour dates, and a printed table that keeps Amharic names.
  const exportColumns = () => {
    const agentNames = {};
    (staticusers || []).forEach((u) => {
      if (["fleetadmin", "callcenteragent", "callcentersupervisor", "scout"].includes(u.usertype)) {
        agentNames[u.id] = nameOf(u);
      }
    });
    const hidden = t("hidden_demo");
    const appText = (d) =>
      d.appVersion
        ? `${d.appVersion}${d.appBuild ? ` (${d.appBuild})` : ""}${d.appPlatform ? ` · ${d.appPlatform === "ios" ? "iOS" : "Android"}` : ""}`
        : "";
    return [
      { title: L("drivers_col_joined", "Joined"), value: (w) => w.d.createdAt, type: "datetime" },
      { title: t("first_name"), value: (w) => w.d.firstName || "" },
      { title: t("last_name"), value: (w) => w.d.lastName || "" },
      { title: t("mobile"), value: (w) => (allowPrivate ? w.d.mobile || "" : hidden) },
      { title: t("email"), value: (w) => (allowPrivate ? w.d.email || "" : hidden) },
      { title: tx.carType, value: (w) => w.d.carType || "" },
      { title: L("plate", "Plate"), value: (w) => w.d.vehicleNumber || "" },
      { title: tx.carInfo, value: (w) => (w.f.carInfo === "has" ? tx.hasCar : w.f.carInfo === "placeholder" ? tx.placeholder : tx.missing) },
      { title: tx.approval, value: (w) => (w.d.approved ? tx.approved : tx.waiting) },
      { title: tx.online, value: (w) => (w.d.driverActiveStatus ? tx.online : tx.offline) },
      { title: tx.onTrip, value: (w) => (w.d.queue ? L("yes", "Yes") : L("no", "No")) },
      { title: L("drivers_col_app", "App"), value: (w) => appText(w.d) },
      { title: tx.licence, value: (w) => (w.f.hasLicence ? tx.ok : tx.missing) },
      { title: tx.tin, value: (w) => (w.f.hasTin ? tx.ok : tx.missing) },
      { title: tx.permissions, value: (w) => (w.f.permReported ? permissionSummary(w.d, L) : "") },
      { title: tx.agent, value: (w) => (w.d.fleetadmin ? agentNames[w.d.fleetadmin] || "" : "") },
    ];
  };
  const handleCsv = () => {
    exportCsv({ columns: exportColumns(), rows: visible, fileName: datedFileName("drivers", "csv") });
  };
  const handlePrint = () => {
    const stamp = formatAddisDateTime(Date.now());
    printTable({
      title: L("drivers", "Drivers"),
      subtitle: [countText, tokenText, stamp].filter(Boolean).join(" · "),
      columns: exportColumns(),
      rows: visible,
      fileName: datedFileName("drivers", ""),
      landscape: true,
      lang: i18n.language,
    });
  };
  const canExport = role === "admin";
  const canCsv = canExport && allowPrivate;

  const columns = [
    { id: "driver", label: L("driver", "Driver"), sort: "driver" },
    { id: "email", label: L("email", "Email"), sort: "email" },
    { id: "joined", label: L("drivers_col_joined", "Joined"), sort: "joined" },
    { id: "car", label: L("drivers_col_car", "Car"), sort: "car" },
    { id: "approved", label: tx.approved, sort: "approved" },
    { id: "online", label: tx.online, sort: "online" },
    { id: "app", label: L("drivers_col_app", "App"), sort: "app" },
    { id: "compliance", label: L("compliance", "Compliance"), sort: "compliance" },
  ];
  const shownColumns = columns.filter((col) => col.id === "driver" || !hiddenCols.includes(col.id));
  const hiddenCount = columns.filter((col) => hiddenCols.includes(col.id)).length;
  const toggleColumn = (colId) =>
    setHiddenCols((prev) => (prev.includes(colId) ? prev.filter((k) => k !== colId) : [...prev, colId]));

  const onSort = (by) =>
    setSort((prev) => (prev.by === by ? { by, dir: prev.dir === "asc" ? "desc" : "asc" } : { by, dir: by === "joined" ? "desc" : "asc" }));

  const pageRows = visible.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);

  const canEdit = EDIT_ROLES.includes(role);
  const canDocuments = DOCUMENT_ROLES.includes(role);
  const canNotes = NOTES_ROLES.includes(role);
  const canDelete = role === "admin";

  const hiddenText = t("hidden_demo");
  const phoneWord = t("phone", { defaultValue: "Phone number" });
  const noteLabel = L("driver_note_add", "Add note");
  const cellText = { fontSize: 14, lineHeight: 1.45, color: c.INK };
  const subText = { fontSize: 13, lineHeight: 1.45, color: c.INK_2 };

  const renderPermissions = (d, f) => {
    if (!f.permReported) {
      return (
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, ...subText, color: c.TEXT_3 }}>
          <HelpOutlineRoundedIcon aria-hidden="true" sx={{ fontSize: 16 }} />
          {L("drivers_perm_not_reported", "Permissions not reported")}
        </Box>
      );
    }
    const list = permissionStatus(d, L);
    const off = list.filter((p) => p.state !== PERM_STATES.ok);
    const checked = d.permCheckedAt
      ? t("drivers_perm_checked", { when: formatAddisDateTime(d.permCheckedAt), defaultValue: "Last checked {{when}}" })
      : "";
    const tip = (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {(off.length ? off : list).map((p) => (
          <span key={p.key}>{`${p.label}: ${p.text}`}</span>
        ))}
        {checked ? <Box component="span" sx={{ opacity: 0.85 }}>{checked}</Box> : null}
      </Box>
    );
    const bad = off.length > 0;
    const Icon = bad ? GppMaybeOutlinedIcon : VerifiedUserOutlinedIcon;
    return (
      <Tooltip title={tip}>
        <Box
          component="span"
          tabIndex={0}
          onClick={stop()}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            fontSize: 13,
            lineHeight: 1.45,
            fontWeight: bad ? 600 : 400,
            color: bad ? c.WARNING_TEXT : c.SUCCESS_TEXT,
            cursor: "help",
            borderRadius: "6px",
            "&:focus-visible": { outline: `2px solid ${c.ACCENT}`, outlineOffset: 2 },
          }}
        >
          <Icon aria-hidden="true" sx={{ fontSize: 16 }} />
          {bad
            ? t("drivers_perm_off", {
                count: off.length,
                defaultValue_one: "{{count}} permission off",
                defaultValue_other: "{{count}} permissions off",
                defaultValue: "{{count}} permissions off",
              })
            : L("drivers_perm_ok", "Permissions OK")}
        </Box>
      </Tooltip>
    );
  };

  const renderCell = (colId, w) => {
    const { d, f } = w;
    switch (colId) {
      case "driver": {
        const name = nameOf(d) || L("no_name", "No name");
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 200 }}>
            {d.profile_image ? (
              <Avatar src={d.profile_image} alt="" sx={{ width: 36, height: 36 }} />
            ) : (
              <Avatar aria-hidden="true" sx={{ width: 36, height: 36, bgcolor: c.SURFACE_ALT, color: c.INK_2, fontSize: 14, fontWeight: 600 }}>
                {initialsOf(d) || "?"}
              </Avatar>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Link
                component={RouterLink}
                to={`/users/driverdetails/${d.id}`}
                state={{ pageNo: safePage }}
                onClick={stop()}
                underline="hover"
                sx={{ display: "block", fontSize: 15, fontWeight: 600, lineHeight: 1.4, color: c.INK, overflowWrap: "anywhere" }}
              >
                {name}
              </Link>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                <Typography component="span" sx={{ ...subText, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                  {allowPrivate ? formatPhone(d.mobile) : hiddenText}
                </Typography>
                {/* Copies +251964540448 (no spaces) for MicroSIP; only where the number is shown. */}
                {allowPrivate && d.mobile ? <CopyButton value={d.mobile} phone what={phoneWord} sx={{ my: -0.75 }} /> : null}
              </Box>
            </Box>
          </Box>
        );
      }
      case "email":
        return <Box sx={{ ...cellText, overflowWrap: "anywhere" }}>{allowPrivate ? d.email || "" : hiddenText}</Box>;
      case "joined":
        return d.createdAt ? (
          <Box sx={{ whiteSpace: "nowrap" }}>
            <Box sx={{ ...cellText, fontWeight: 600 }}>{formatAddisDate(d.createdAt)}</Box>
            <Box sx={subText}>{formatAddisTime(d.createdAt)}</Box>
          </Box>
        ) : (
          <Box sx={{ ...subText, color: c.TEXT_3 }}>—</Box>
        );
      case "car":
        if (f.carInfo === "placeholder") {
          return (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
              <Chip
                size="small"
                icon={<DirectionsCarRoundedIcon />}
                label={tx.placeholder}
                sx={{ ...toneChipSx("amber"), height: 26, fontSize: 13 }}
              />
              <Box sx={{ ...subText, whiteSpace: "nowrap" }}>{`${d.carType} · ${L("drivers_no_plate", "no plate")}`}</Box>
            </Box>
          );
        }
        if (d.carType || d.vehicleNumber) {
          return (
            <Box sx={{ whiteSpace: "nowrap" }}>
              <Box sx={{ ...cellText, fontWeight: 600 }}>{d.carType || "—"}</Box>
              <Box sx={{ ...subText, fontVariantNumeric: "tabular-nums", letterSpacing: "0.02em" }}>
                {d.vehicleNumber || L("drivers_no_plate", "no plate")}
              </Box>
            </Box>
          );
        }
        return <Box sx={{ ...subText, color: c.TEXT_3 }}>{L("drivers_no_car", "No car")}</Box>;
      case "approved":
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              size="small"
              icon={d.approved ? <CheckRoundedIcon /> : <ScheduleRoundedIcon />}
              label={d.approved ? tx.approved : tx.waiting}
              sx={{ ...toneChipSx(d.approved ? "success" : "warning"), height: 28, fontSize: 13 }}
            />
            {canSwitch ? (
              <Switch
                size="small"
                checked={!!d.approved}
                onClick={stop()}
                onChange={() => handelApproved(d)}
                inputProps={{ "aria-label": `${t("account_approve")}: ${nameOf(d)}` }}
              />
            ) : null}
          </Box>
        );
      case "online": {
        const online = !!d.driverActiveStatus;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, whiteSpace: "nowrap" }}>
                <Box
                  aria-hidden="true"
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    flex: "none",
                    ...(online ? { bgcolor: c.SUCCESS } : { border: `2px solid ${c.TEXT_3}` }),
                  }}
                />
                <Box sx={{ ...cellText, fontWeight: online ? 600 : 400, color: online ? c.INK : c.INK_2 }}>
                  {online ? tx.online : tx.offline}
                </Box>
              </Box>
              {online || d.queue ? (
                <Box sx={{ ...subText, pl: 2.25, whiteSpace: "nowrap", ...(d.queue ? { color: c.INFO_TEXT, fontWeight: 600 } : { color: c.TEXT_3 }) }}>
                  {d.queue ? tx.onTrip : tx.free}
                </Box>
              ) : null}
            </Box>
            {canSwitch ? (
              <Switch
                size="small"
                checked={online}
                onClick={stop()}
                onChange={() => handelDriverActiveStatus(d)}
                inputProps={{ "aria-label": `${t("driver_active")}: ${nameOf(d)}` }}
              />
            ) : null}
          </Box>
        );
      }
      case "app":
        // appVersion / appBuild / appPlatform are stamped on /users/{uid} by
        // AppCommon once per app launch; no value = the driver has not
        // launched a build that reports it yet.
        return d.appVersion ? (
          <Box sx={{ whiteSpace: "nowrap" }}>
            <Box sx={{ ...cellText, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{d.appVersion}</Box>
            <Box sx={subText}>
              {[d.appPlatform ? (d.appPlatform === "ios" ? "iOS" : "Android") : "", d.appBuild ? String(d.appBuild) : ""]
                .filter(Boolean)
                .join(" · ")}
            </Box>
          </Box>
        ) : (
          <Box sx={{ ...subText, color: c.TEXT_3, whiteSpace: "nowrap" }}>{L("drivers_app_not_reported", "Not reported")}</Box>
        );
      case "compliance":
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, whiteSpace: "nowrap" }}>
              <DocWord ok={f.hasLicence} label={tx.licence} okText={tx.ok} missingText={tx.missing} />
              <DocWord ok={f.hasTin} label={tx.tin} okText={tx.ok} missingText={tx.missing} />
            </Box>
            {renderPermissions(d, f)}
          </Box>
        );
      default:
        return null;
    }
  };

  const actionSlot = { width: 40, height: 40, flex: "none" };
  const renderActions = (d) => (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
      {/* The call centre's main action: one click to the driver's notes with
          the box ready (was ... > Communication history > expand > pick). */}
      {canNotes ? (
        <Tooltip title={noteLabel}>
          <IconButton aria-label={`${noteLabel}: ${nameOf(d)}`} onClick={stop(() => setNotesId(d.id))} sx={{ width: 40, height: 40, p: 0 }}>
            <IconCircle icon={<AddCommentOutlinedIcon />} hue="info" size={34} />
          </IconButton>
        </Tooltip>
      ) : null}
      {canEdit ? (
        <Tooltip title={t("edit")}>
          <IconButton aria-label={`${t("edit")}: ${nameOf(d)}`} onClick={stop(() => goEdit(d))}>
            <EditOutlinedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Box sx={actionSlot} />
      )}
      {canDocuments ? (
        <Tooltip title={t("documents")}>
          <IconButton aria-label={`${t("documents")}: ${nameOf(d)}`} onClick={stop(() => goDocuments(d))}>
            <DescriptionOutlinedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Box sx={actionSlot} />
      )}
      <Tooltip title={L("drivers_more_actions", "More actions")}>
        <IconButton
          aria-label={`${L("drivers_more_actions", "More actions")}: ${nameOf(d)}`}
          aria-haspopup="menu"
          onClick={stop((e) => setRowMenu({ anchor: e.currentTarget, row: d }))}
        >
          <MoreHorizRoundedIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const colSpan = shownColumns.length + 1;

  const renderEmpty = () => {
    if (total === 0) {
      return (
        <EmptyState
          icon={<GroupsOutlinedIcon />}
          title={L("drivers_empty_none", "No drivers yet")}
          body={L("drivers_empty_none_body", "Drivers appear here after they sign up in the app or you add one.")}
          actionLabel={t("add_driver")}
          onAction={() => navigate("/users/edituser/driver", navState)}
        />
      );
    }
    if (activeTokens.length === 0 && searchText) {
      return (
        <EmptyState
          icon={<SearchOffRoundedIcon />}
          title={t("drivers_empty_search", { query: searchText, defaultValue: "No drivers match “{{query}}”" })}
          body={L("drivers_empty_search_body", "Try part of the name, the phone number or the plate.")}
          actionLabel={L("clear_search", "Clear search")}
          onAction={() => setSearch("")}
        />
      );
    }
    return (
      <EmptyState
        icon={<FilterAltOffOutlinedIcon />}
        title={t("drivers_empty_filtered", {
          count: tokens.length,
          defaultValue_one: "No drivers match this filter",
          defaultValue_other: "No drivers match these {{count}} filters",
          defaultValue: "No drivers match these {{count}} filters",
        })}
        body={tokenText}
        actionLabel={L("clear_filters", "Clear filters")}
        onAction={clearAll}
      />
    );
  };

  const menuRow = rowMenu ? rowMenu.row : null;

  return (
    <Box dir={i18n.dir()} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: 1.5 }}>
        {ONBOARD_ROLES.includes(role) ? (
          <Button
            variant="outlined"
            color="neutral"
            startIcon={<PersonAddAltRoundedIcon />}
            onClick={() => navigate("/onboard-driver", navState)}
          >
            {t("unified_driver_onboarding")}
          </Button>
        ) : null}
        <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => navigate("/users/edituser/driver", navState)}>
          {t("add_driver")}
        </Button>
      </Box>

      <Paper sx={{ p: { xs: 2, md: 2.5 }, display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
          <SearchField value={search} onChange={setSearch} placeholder={L("drivers_search_hint", "Search name, phone or email")} />
          <FilterToggleButton
            open={filtersOpen}
            onClick={toggleFilters}
            activeCount={activeTokens.length}
            controls="drivers-filter-groups"
            sx={{ width: { xs: "100%", sm: "auto" } }}
          />
        </Box>
        <Collapse in={filtersOpen} timeout="auto">
          <Box id="drivers-filter-groups" sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 2 }}>
            <ChipGroup label={groups.carType.label} value={filters.carType} options={groups.carType.options} onChange={(v) => setFilter("carType", v)} />
            <Box sx={{ display: "flex", flexWrap: "wrap", rowGap: 1.5, columnGap: 4 }}>
              <ChipGroup label={groups.approval.label} value={filters.approval} options={groups.approval.options} onChange={(v) => setFilter("approval", v)} />
              <ChipGroup label={groups.online.label} value={filters.online} options={groups.online.options} onChange={(v) => setFilter("online", v)} />
            </Box>
            <ChipGroup label={groups.carInfo.label} value={filters.carInfo} options={groups.carInfo.options} onChange={(v) => setFilter("carInfo", v)} />
            <Divider />
            <Box role="group" aria-label={tx.more} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
              <Typography component="span" sx={{ fontSize: 15, fontWeight: 600, color: c.INK_2, lineHeight: 1.4, mr: 0.5 }}>
                {tx.more}
              </Typography>
              {MORE_FILTER_KEYS.map((k) => (
                <DropChip key={k} label={groups[k].label} value={filters[k]} options={groups[k].options} onChange={(v) => setFilter(k, v)} />
              ))}
            </Box>
          </Box>
        </Collapse>
      </Paper>

      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.25 }}>
        <Typography role="status" aria-live="polite" sx={{ fontSize: 15, fontWeight: 700, color: c.INK, lineHeight: 1.4, mr: 0.5 }}>
          {listLoading ? L("loading_drivers", "Loading drivers…") : countText}
        </Typography>
        {tokens.length ? <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} /> : null}
        {tokens.map((tk) => (
          <Chip
            key={tk.key}
            aria-label={t("drivers_remove_filter", { label: `${tk.group}: ${tk.value}`, defaultValue: "Remove filter {{label}}" })}
            label={
              <span>
                {`${tk.group}: `}
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {tk.value}
                </Box>
              </span>
            }
            onDelete={() => removeToken(tk.key)}
            deleteIcon={<CloseRoundedIcon />}
            sx={{
              height: 36,
              borderRadius: "18px",
              bgcolor: c.SURFACE_ALT,
              color: c.INK,
              fontSize: 14,
              fontWeight: 400,
              "& .MuiChip-deleteIcon": { color: c.INK_2, bgcolor: c.SURFACE, borderRadius: "50%", fontSize: 20, p: "2px" },
              "& .MuiChip-deleteIcon:hover": { color: c.INK },
            }}
          />
        ))}
        {tokens.length ? (
          <Button variant="text" onClick={clearAll}>
            {L("clear_all", "Clear all")}
          </Button>
        ) : null}
        <Box sx={{ flex: 1 }} />
        {canExport ? (
          <Button variant="outlined" color="neutral" startIcon={<PrintOutlinedIcon />} onClick={handlePrint} disabled={listLoading || !visible.length}>
            {L("export_print_pdf", "Print or save PDF")}
          </Button>
        ) : null}
        {canCsv ? (
          <Button variant="outlined" color="neutral" startIcon={<DownloadRoundedIcon />} onClick={handleCsv} disabled={listLoading || !visible.length}>
            {L("export_download_csv", "Download CSV")}
          </Button>
        ) : null}
        <Button
          variant="outlined"
          color="neutral"
          startIcon={<ViewColumnOutlinedIcon />}
          aria-haspopup="menu"
          aria-expanded={columnsAnchor ? "true" : "false"}
          onClick={(e) => setColumnsAnchor(e.currentTarget)}
        >
          {L("drivers_columns", "Columns")}
          {hiddenCount ? (
            <Box component="span" sx={{ ml: 0.75, fontWeight: 400, color: c.INK_2 }}>
              {t("drivers_columns_hidden", { count: hiddenCount, defaultValue: "{{count}} hidden" })}
            </Box>
          ) : null}
        </Button>
        <Menu anchorEl={columnsAnchor} open={Boolean(columnsAnchor)} onClose={() => setColumnsAnchor(null)}>
          {columns
            .filter((col) => col.id !== "driver")
            .map((col) => (
              <MenuItem
                key={col.id}
                role="menuitemcheckbox"
                aria-checked={hiddenCols.includes(col.id) ? "false" : "true"}
                onClick={() => toggleColumn(col.id)}
                sx={{ minWidth: 200 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox edge="start" size="small" checked={!hiddenCols.includes(col.id)} tabIndex={-1} disableRipple inputProps={{ "aria-hidden": "true" }} />
                </ListItemIcon>
                <ListItemText primary={col.label} />
              </MenuItem>
            ))}
        </Menu>
      </Box>

      {listFailed ? (
        <InlineMessage
          kind="error"
          title={L("drivers_load_failed", "Could not load drivers.")}
          actionLabel={L("retry", "Retry")}
          onAction={() => dispatch(fetchUsersOnce())}
        >
          {L("load_failed_body", "Check the internet connection, then try again.")}
        </InlineMessage>
      ) : null}

      <Paper sx={{ overflow: "hidden" }}>
        {/* position: relative keeps the 1-px screen-reader words of the cells
            inside the scrolling table: without it they sat past the card and
            widened the whole page (a sideways scroll, and on a phone the
            full-screen dialogs grew wider than the screen). Cells are 12 px
            apart (16 at the card edges) so the four row actions fit at 1440. */}
        <TableContainer sx={{ position: "relative" }}>
          <Table
            aria-label={L("drivers", "Drivers")}
            sx={{
              minWidth: 960,
              "& .MuiTableCell-root": { px: 1.5 },
              "& .MuiTableCell-root:first-of-type": { pl: 2 },
              "& .MuiTableCell-root:last-of-type": { pr: 2 },
            }}
          >
            <TableHead>
              <TableRow>
                {shownColumns.map((col) => (
                  <TableCell key={col.id} sortDirection={sort.by === col.sort ? sort.dir : false} sx={{ fontSize: 13, whiteSpace: "nowrap" }}>
                    <TableSortLabel active={sort.by === col.sort} direction={sort.by === col.sort ? sort.dir : "asc"} onClick={() => onSort(col.sort)}>
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ width: canNotes ? 192 : 150 }}>
                  <Box component="span" sx={srOnly}>
                    {t("actions")}
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listLoading ? (
                <TableSkeletonRows rows={6} columns={colSpan} rowHeight={64} />
              ) : pageRows.length ? (
                pageRows.map((w) => (
                  <TableRow
                    key={w.d.id}
                    hover
                    // Dragging across a number or a name to copy it does not
                    // open the driver (call centre, 29 Oct); a plain click does.
                    onMouseDown={(e) => {
                      rowPressRef.current = pressPoint(e);
                    }}
                    onClick={(e) => {
                      if (!selectingTextIn(e.currentTarget, e, rowPressRef.current)) goDetails(w.d);
                    }}
                    sx={{ cursor: "pointer", "& > td": { py: 1.25, verticalAlign: "middle" } }}
                  >
                    {shownColumns.map((col) => (
                      <TableCell key={col.id}>{renderCell(col.id, w)}</TableCell>
                    ))}
                    <TableCell align="right">{renderActions(w.d)}</TableCell>
                  </TableRow>
                ))
              ) : listFailed ? null : (
                <TableRow>
                  <TableCell colSpan={colSpan} sx={{ borderBottom: 0 }}>
                    {renderEmpty()}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!listLoading && visible.length ? (
          <TablePagination
            component="div"
            count={visible.length}
            page={safePage}
            onPageChange={(e, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={ROWS_OPTIONS}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10) || 25);
              setPage(0);
            }}
            labelRowsPerPage={t("rows_per_page", { defaultValue: "Rows per page" })}
            labelDisplayedRows={({ from, to, count }) => `${fmt(from)}–${fmt(to)} ${t("of", { defaultValue: "of" })} ${fmt(count)}`}
            getItemAriaLabel={(type) =>
              type === "next"
                ? t("next_page_tooltip", { defaultValue: "Next page" })
                : type === "previous"
                  ? t("previous_page_tooltip", { defaultValue: "Previous page" })
                  : type
            }
            sx={{
              borderTop: `1px solid ${c.OUTLINE}`,
              // On a 360-px phone the controls wrap instead of running off
              // the card (the next-page arrow was cut off).
              "& .MuiTablePagination-toolbar": { flexWrap: "wrap", rowGap: 0.5, pl: { xs: 1, sm: 2 } },
              "& .MuiTablePagination-spacer": { display: { xs: "none", sm: "block" } },
            }}
          />
        ) : null}
      </Paper>

      <Menu anchorEl={rowMenu ? rowMenu.anchor : null} open={Boolean(rowMenu)} onClose={closeRowMenu}>
        <MenuItem
          onClick={() => {
            const row = menuRow;
            closeRowMenu();
            if (row) goDetails(row);
          }}
        >
          <ListItemIcon>
            <PersonOutlineRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("profile_page_subtitle")} />
        </MenuItem>
        {canNotes ? (
          <MenuItem
            onClick={() => {
              const row = menuRow;
              closeRowMenu();
              setNotesId(row ? row.id : null);
            }}
          >
            <ListItemIcon>
              <ForumOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={L("communication_history", "Communication history")} />
          </MenuItem>
        ) : null}
        {canSwitch && menuRow ? (
          <MenuItem
            onClick={() => {
              const row = menuRow;
              closeRowMenu();
              if (row) handleQueueStatus(row);
            }}
          >
            <ListItemIcon>
              <DirectionsCarRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={menuRow.queue ? L("drivers_mark_free", "Mark as free") : L("drivers_mark_on_trip", "Mark as on a trip")} />
          </MenuItem>
        ) : null}
        {canDelete ? <Divider /> : null}
        {canDelete ? (
          <MenuItem onClick={() => menuRow && handleDelete(menuRow)} sx={{ color: c.DANGER_TEXT }}>
            <ListItemIcon>
              <DeleteOutlineRoundedIcon fontSize="small" sx={{ color: c.DANGER_TEXT }} />
            </ListItemIcon>
            <ListItemText primary={L("drivers_delete", "Delete driver")} />
          </MenuItem>
        ) : null}
      </Menu>

      <QuickDriverNote driver={notesRow} open={Boolean(notesRow)} onClose={() => setNotesId(null)} allowPrivate={allowPrivate} />
    </Box>
  );
}
